/**
 * Reads the configuration off the host <script> tag's data-* attributes.
 *
 * Required:
 *   data-key          — Widget API key (kl_pub_…; older keys dvf_pub_…)
 *   data-client-id    — Client UUID this workspace request attaches to
 *
 * Optional:
 *   data-enabled      — Client-side master switch. Set to the literal "false"
 *                       to skip mount entirely (script still loads, but
 *                       bootstrap exits before any DOM is created). Any other
 *                       value or unset keeps the widget enabled.
 *   data-api-url      — Override API origin (defaults to script's origin)
 *   data-color        — Brand color hex for the launcher and the Voice button.
 *                       Default: the firm's accent (client override → workspace
 *                       → template palette, from the pre-flight call), then the
 *                       Klientele neutrals.
 *   data-position     — bottom-right | bottom-left | top-right | top-left (default: bottom-right)
 *   data-label        — Floating button label (default: "Help")
 *   data-launcher     — pill | icon (default: pill). "icon" renders a round,
 *                       icon-only 56px button (label kept as its accessible
 *                       name and tooltip) — sits alongside chat bubbles.
 *   data-title        — Panel heading (default: "Tell <firm name>")
 *   data-success      — Extra line on the "after sending" screen (default: none)
 *   data-icon-url     — URL to a custom icon (PNG/SVG). When unset, the Klientele logo renders.
 *   data-require-email — true|false (default: false)
 *   data-default-urgency — critical|high|medium|low. Pre-selects the urgency
 *                       chip that files at this urgency. Default: the
 *                       template's own default ("Slowing me down").
 *   data-urgency-labels — Four labels for the urgency chips, least to most
 *                       urgent, separated by "|". Default:
 *                       "Can wait|Slowing me down|Blocking me|Everything's down".
 *                       The chips map onto the template's urgencies:
 *                       lowest, the default, second-highest, highest.
 *   data-hide-on      — Comma-separated path patterns to HIDE on (denylist). Supports * wildcard.
 *   data-show-on      — Comma-separated path patterns to SHOW on (allowlist). Mutually exclusive with hide-on.
 *   data-pointer-color — Hex color for the recording highlight pointer (default: #dc2626 — red)
 */

export type Position = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
export type LauncherStyle = 'pill' | 'icon';
export type Urgency = 'critical' | 'high' | 'medium' | 'low';

export type UrgencyKey = 'can_wait' | 'slowing' | 'blocking' | 'down';

export const URGENCY_ORDER: readonly UrgencyKey[] = ['can_wait', 'slowing', 'blocking', 'down'] as const;
export const DEFAULT_URGENCY_LABELS: Record<UrgencyKey, string> = {
  can_wait: 'Can wait',
  slowing: 'Slowing me down',
  blocking: 'Blocking me',
  down: "Everything's down",
};

/** What the server's /widget/config pre-flight returns (FR-2026-0105). */
export interface ServerWidgetConfig {
  firm: { name: string; logoUrl: string | null; accentColor: string | null };
  nouns: { request: string; requestPlural: string; referencePrefix: string };
  urgency: Array<{ key: UrgencyKey; urgency: Urgency; urgencyLabel: string }>;
  defaultUrgency: UrgencyKey;
  voiceNotes: { enabled: boolean; maxMs: number; maxBytes: number; mimeTypes: string[] };
}

export interface WidgetConfig {
  /**
   * When false the widget MUST NOT mount, MUST NOT fire the pre-flight
   * config call, and MUST NOT attach any DOM. Set client-side via
   * `data-enabled="false"` on the host script tag. Any other value is
   * treated as enabled (matches every existing embed).
   */
  enabled: boolean;
  apiUrl: string;
  apiKey: string;
  clientId: string;
  /**
   * Optional engagement linking. When set, every request created by this widget
   * instance is attached to the named engagement. Backend silently drops the
   * field on non-Agency plans (gated server-side, never trust the client).
   */
  engagementId: string | null;
  /** data-color, or null to take the firm's accent from the server. */
  color: string | null;
  position: Position;
  launcher: LauncherStyle;
  label: string;
  /** data-title, or null for "Tell <firm>". */
  title: string | null;
  /** data-success, or null. */
  successMessage: string | null;
  iconUrl: string | null;
  requireEmail: boolean;
  /** data-default-urgency, or null for the template's default. */
  defaultUrgency: Urgency | null;
  /** Chip labels, data-urgency-labels over the defaults. */
  urgencyLabels: Record<UrgencyKey, string>;
  hidePatterns: string[];
  showPatterns: string[];
  pointerColor: string;
  /** Filled from the server's /widget/config pre-flight, not the script tag. */
  server: ServerWidgetConfig;
}

const POSITIONS: readonly Position[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const;
const URGENCIES: readonly Urgency[] = ['critical', 'high', 'medium', 'low'] as const;

function asBool(raw: string | null, fallback: boolean): boolean {
  if (raw == null) return fallback;
  return raw.toLowerCase() === 'true' || raw === '1';
}

function asPosition(raw: string | null): Position {
  if (raw && (POSITIONS as readonly string[]).includes(raw)) return raw as Position;
  return 'bottom-right';
}

function asLauncher(raw: string | null): LauncherStyle {
  return raw === 'icon' ? 'icon' : 'pill';
}

function asUrgency(raw: string | null): Urgency | null {
  if (raw && (URGENCIES as readonly string[]).includes(raw)) return raw as Urgency;
  return null;
}

function asColor(raw: string | null): string | null {
  if (raw && /^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  return null;
}

/** "a|b|c|d" → the four chip labels; blanks and a short list keep the defaults. */
function asUrgencyLabels(raw: string | null): Record<UrgencyKey, string> {
  const out = { ...DEFAULT_URGENCY_LABELS };
  if (!raw) return out;
  const parts = raw.split('|').map((p) => p.trim());
  URGENCY_ORDER.forEach((key, i) => {
    const v = parts[i];
    if (v) out[key] = v.slice(0, 40);
  });
  return out;
}

/** Placeholder until the pre-flight answers; never rendered. */
export const EMPTY_SERVER_CONFIG: ServerWidgetConfig = {
  firm: { name: '', logoUrl: null, accentColor: null },
  nouns: { request: 'Request', requestPlural: 'Requests', referencePrefix: '' },
  urgency: [],
  defaultUrgency: 'slowing',
  voiceNotes: { enabled: false, maxMs: 0, maxBytes: 0, mimeTypes: [] },
};

function asPatternList(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/** Resolves the API origin from data-api-url, falling back to the script's own origin. */
function resolveApiUrl(script: HTMLScriptElement, override: string | null): string {
  if (override) return override.replace(/\/$/, '');
  try {
    const u = new URL(script.src, window.location.href);
    return `${u.protocol}//${u.host}`;
  } catch {
    return window.location.origin;
  }
}

/**
 * Find the host <script> tag. Looks for the script that loaded this bundle
 * by matching `src` ending in `/widget/v1/klientele.js`. Falls back to
 * `document.currentScript` when available.
 */
export function findHostScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src*="/widget/v1/klientele.js"]');
  return scripts.length > 0 ? (scripts[scripts.length - 1] ?? null) : null;
}

export function readConfig(script: HTMLScriptElement): WidgetConfig | { error: string } {
  const apiKey = script.getAttribute('data-key');
  const clientId = script.getAttribute('data-client-id');

  if (!apiKey) return { error: 'Klientele widget: data-key attribute is required' };
  if (!clientId) return { error: 'Klientele widget: data-client-id attribute is required' };

  // Light shape check on engagementId — must look like a UUID. If it's
  // malformed, drop silently rather than send a bad value to the backend
  // (which would reject with 400 and look like the widget is broken).
  const rawEngagementId = script.getAttribute('data-engagement-id');
  const engagementId = rawEngagementId && /^[0-9a-fA-F-]{36}$/.test(rawEngagementId)
    ? rawEngagementId
    : null;

  // Master switch — the widget's own client-side kill. Frontend embedders
  // set `data-enabled="false"` on the host script when they want to
  // disable the widget for a specific build without unpublishing the API
  // key or origin allowlist. Any other value is treated as enabled.
  const enabledRaw = script.getAttribute('data-enabled');
  const enabled = enabledRaw == null || enabledRaw.toLowerCase() !== 'false';

  return {
    enabled,
    apiUrl: resolveApiUrl(script, script.getAttribute('data-api-url')),
    apiKey,
    clientId,
    engagementId,
    color: asColor(script.getAttribute('data-color')),
    position: asPosition(script.getAttribute('data-position')),
    launcher: asLauncher(script.getAttribute('data-launcher')),
    label: script.getAttribute('data-label') ?? 'Help',
    title: script.getAttribute('data-title') || null,
    successMessage: script.getAttribute('data-success') || null,
    iconUrl: script.getAttribute('data-icon-url'),
    requireEmail: asBool(script.getAttribute('data-require-email'), false),
    defaultUrgency: asUrgency(script.getAttribute('data-default-urgency')),
    urgencyLabels: asUrgencyLabels(script.getAttribute('data-urgency-labels')),
    hidePatterns: asPatternList(script.getAttribute('data-hide-on')),
    showPatterns: asPatternList(script.getAttribute('data-show-on')),
    pointerColor: asHexColorOr(script.getAttribute('data-pointer-color'), '#dc2626'),
    server: EMPTY_SERVER_CONFIG,
  };
}

function asHexColorOr(raw: string | null, fallback: string): string {
  if (raw && /^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  return fallback;
}

const isStr = (v: unknown): v is string => typeof v === 'string';
const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Validate the pre-flight response. Returns null when it is not the shape
 * this bundle understands (an older backend), so the widget stays hidden
 * rather than rendering with missing nouns.
 */
export function parseServerConfig(raw: unknown): ServerWidgetConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, any>;
  const firm = d.firm ?? {};
  const nouns = d.nouns ?? {};
  const voice = d.voiceNotes ?? {};
  if (!isStr(firm.name) || !isStr(nouns.request) || !Array.isArray(d.urgency)) return null;
  const urgency = (d.urgency as Array<Record<string, unknown>>)
    .filter((u) => (URGENCY_ORDER as readonly unknown[]).includes(u.key) && (URGENCIES as readonly unknown[]).includes(u.urgency))
    .map((u) => ({ key: u.key as UrgencyKey, urgency: u.urgency as Urgency, urgencyLabel: isStr(u.urgencyLabel) ? u.urgencyLabel : '' }));
  if (urgency.length !== URGENCY_ORDER.length) return null;
  const logo = isStr(firm.logoUrl) && /^https?:\/\//.test(firm.logoUrl) ? firm.logoUrl : null;
  return {
    firm: { name: firm.name, logoUrl: logo, accentColor: isStr(firm.accentColor) && HEX.test(firm.accentColor) ? firm.accentColor : null },
    nouns: {
      request: nouns.request,
      requestPlural: isStr(nouns.requestPlural) ? nouns.requestPlural : nouns.request,
      referencePrefix: isStr(nouns.referencePrefix) ? nouns.referencePrefix : '',
    },
    urgency,
    defaultUrgency: (URGENCY_ORDER as readonly unknown[]).includes(d.defaultUrgency) ? d.defaultUrgency : 'slowing',
    voiceNotes: {
      enabled: voice.enabled === true,
      maxMs: typeof voice.maxMs === 'number' ? voice.maxMs : 0,
      maxBytes: typeof voice.maxBytes === 'number' ? voice.maxBytes : 0,
      mimeTypes: Array.isArray(voice.mimeTypes) ? voice.mimeTypes.filter(isStr) : [],
    },
  };
}
